import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import { Typography, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { orange, yellow } from '@mui/material/colors';
import * as eventHelper from '../../helpers/event-helper';

const renderDelay = (startTime, endTime) => {
  const { sign, hours, minutes } = eventHelper.getTimeDifferenceData(
    startTime,
    endTime
  );
  return `${sign > 0 ? '' : '-'}${hours > 0 ? hours + 'h ' : ''}${minutes}m`;
};

const getProgressPercentage = (start, current, end) => {
  return ((current - start) / (end - start)) * 100;
};

const useStyles = makeStyles(theme => ({
  progressBar: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  },
  progress: {
    content: ' ',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  countdown: {
    zIndex: 10,
    lineHeight: 1,
  },
  TTG: {
    marginLeft: theme.spacing(2),
  },
  postFlightContent: {
    display: 'flex',
  },
}));

const DepartureProgress = ({ STA, STD, ETA, ETD, ATA, ATD, tab }) => {
  const classes = useStyles();
  const theme = useTheme();

  const [timeRemaining, setTimeRemaining] = useState(null);

  const timer = useRef();
  const startSTA = moment(STA).subtract(3, 'hours');
  const startSTD = moment(STD).subtract(3, 'hours');
  const end = moment(tab === 0 ? ETA : ETD);
  const isInRange = moment().isBefore(end);
  const showPostFlight = (ATA && tab === 0) || (ATD && tab === 1);
  const isEarlyTimer =
    (moment().isBefore(startSTA) && tab === 0) ||
    (moment().isBefore(startSTD) && tab === 1);

  const renderTotalTimeOnGround = (startTime, endTime) => {
    const { hours, minutes } = eventHelper.getTimeDifferenceData(
      startTime,
      endTime
    );

    return (
      <div className={classes.TTG}>
        TTG: {hours > 0 ? hours + 'h ' : ''}
        {minutes}m
      </div>
    );
  };

  const renderPostFlightContent = () => {
    const inboundDelayTime = renderDelay(STA, ATA);
    const outboundDelayTime = renderDelay(STD, ATD);

    const totalTimeOnGround = renderTotalTimeOnGround(ATA, ATD);

    return (
      <div className={classes.postFlightContent}>
        <div>DL time: {tab === 0 ? inboundDelayTime : outboundDelayTime}</div>{' '}
        {ATA && ATD && tab === 1 ? totalTimeOnGround : ''}
      </div>
    );
  };

  useEffect(() => {
    if (timer.current) {
      clearInterval(timer.current);
    }

    const interval = setInterval(() => {
      const difference = moment.duration(
        isInRange ? end.diff(moment()) : moment().diff(end)
      );

      setTimeRemaining(difference);
    }, 1000);

    timer.current = interval;

    return () => clearInterval(interval);
  }, [ETA, ETD, tab, end, isInRange]);

  return (
    <div className={classes.progressBar}>
      {timeRemaining && (
        <>
          <div
            className={classes.progress}
            style={{
              width: `${
                isInRange && !showPostFlight && !isEarlyTimer
                  ? getProgressPercentage(
                      (tab === 0 ? startSTA : startSTD).valueOf(),
                      moment().valueOf(),
                      end.valueOf()
                    )
                  : 100
              }%`,
              backgroundColor: showPostFlight
                ? theme.palette.success.main
                : isInRange
                  ? isEarlyTimer
                    ? yellow[400]
                    : orange[300]
                  : theme.palette.error.main,
            }}
          ></div>
          <Typography
            style={{
              color:
                isInRange && !showPostFlight
                  ? theme.palette.text.main
                  : 'white',
            }}
            className={classes.countdown}
            variant="subtitle2"
          >
            {showPostFlight ? (
              renderPostFlightContent()
            ) : (
              <>
                {isInRange ? '-' : '+'}
                {timeRemaining.years() ? timeRemaining.years() + 'y ' : null}
                {timeRemaining.months() ? timeRemaining.months() + 'm ' : null}
                {timeRemaining.weeks() ? timeRemaining.weeks() + 'w ' : null}
                {timeRemaining.days() ? timeRemaining.days() + 'd ' : null}
                {timeRemaining.hours() ? timeRemaining.hours() + 'h ' : ''}
                {timeRemaining.minutes() ? timeRemaining.minutes() + 'm ' : ''}
                {`${timeRemaining.seconds()}s`}
              </>
            )}
          </Typography>
        </>
      )}
    </div>
  );
};

export default DepartureProgress;
